import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {ReviewDetailsFragment} from '../../../schema-types/ReviewDetailsFragment';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {getUrgencyInitialValues} from '../hooks/createBuilderHooks';
import OWNERSHIP from '../../../constants/Ownership';
import {
  BUILDER_TYPES,
  SPP_BUILDER_TYPES,
  BuilderTypeValue,
  SppBuilderTypeValue,
} from '../constants/SolutionType';
import {getSetUpNewInitialValues} from '../hooks/builderSetUpNewHooks';
import {getContributeInitialValues} from '../hooks/builderContributeHooks';
import {getModifyInitialValues} from '../hooks/builderModifyHooks';
import {getSavedWithdrawalValues} from '../hooks/builderWithdrawalHooks';
import {getTransferInitialValues} from '../hooks/builderTransferHooks';
import {getAnnuityInitialValues} from '../hooks/builderAnnuityHooks';
import {getProtectionInitialValues} from '../hooks/builderProtectionHooks';
import {mainPlanTypeType, builderFieldsType} from './builderFields';

export interface GetBuilderInitialValuesArgs {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  reviewDetails: ReviewDetailsFragment | undefined;
  searchParams: URLSearchParams;
  isSppDa: boolean;
}

const resolveHow = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  reviewDetails: ReviewDetailsFragment | undefined,
  searchParams: URLSearchParams,
  isSppDa: boolean
): BuilderTypeValue | undefined => {
  // SPP DA is always an instruction flow
  if (isSppDa) {
    return BUILDER_TYPES.Instruction;
  }
  if (builderDetails) {
    return builderDetails.isXo ? BUILDER_TYPES.Instruction : BUILDER_TYPES.Recommendation;
  }
  if (reviewDetails?.executionOnly) {
    return BUILDER_TYPES.Instruction;
  }
  if (reviewDetails) {
    return BUILDER_TYPES.Recommendation;
  }
  return (searchParams.get('how') as BuilderTypeValue | null) ?? undefined;
};

const resolveContact = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  searchParams: URLSearchParams
): string | undefined => {
  if (builderDetails) {
    return builderDetails.contact?.id ?? OWNERSHIP.JOINT_CODE;
  }
  return searchParams.get('contact') ?? undefined;
};

const resolveSppType = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  searchParams: URLSearchParams
): SppBuilderTypeValue | undefined => {
  if (builderDetails) {
    return builderDetails.isSppDaXo
      ? SPP_BUILDER_TYPES.ExecutionOnly
      : SPP_BUILDER_TYPES.Advised;
  }
  return (searchParams.get('sppType') as SppBuilderTypeValue | null) ?? undefined;
};

export const getBuilderInitialValues = ({
  builderDetails,
  reviewDetails,
  searchParams,
  isSppDa,
}: GetBuilderInitialValuesArgs): Partial<builderFieldsType> => {
  const planAction =
    builderDetails?.action ??
    (searchParams.get('planAction') as OVRecommendActionEnum | null) ??
    undefined;

  const mainPlanType =
    (builderDetails?.planType as builderFieldsType['mainPlanType']) ??
    (searchParams.get('mainPlanType') as mainPlanTypeType | null) ??
    undefined;

  return {
    isSppDa,
    isSpp: reviewDetails?.isSpp ?? false,
    planAction,
    mainPlanType,
    how: resolveHow(builderDetails, reviewDetails, searchParams, isSppDa),
    sppType: resolveSppType(builderDetails, searchParams),
    contact: resolveContact(builderDetails, searchParams),
    ...getUrgencyInitialValues(builderDetails),
    investorCategory: reviewDetails?.account?.investorCategory || undefined,
    setUpNew: getSetUpNewInitialValues(builderDetails),
    contribute: getContributeInitialValues(builderDetails, isSppDa),
    modify: getModifyInitialValues(builderDetails),
    withdraw: getSavedWithdrawalValues(builderDetails),
    transfer: getTransferInitialValues(builderDetails, isSppDa),
    annuity: getAnnuityInitialValues(builderDetails, isSppDa),
    protectionPlan: getProtectionInitialValues(builderDetails),
  };
};
